import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from 'atoms/Button';
import { pluralize } from 'inflection';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useStore } from 'stores/store';
import { logEvent } from 'utils/analytics';
import { IFilter, IFilterOptionsItem } from 'utils/globalTypes';

import s from './FiltersSidebar.module.scss';
import FiltersSidebarItem from './FiltersSidebarItem';

interface FiltersSidebar {
  isOpen: boolean;
  showCounts?: boolean;
  isMobile?: boolean;
  onClose: () => void;
  filters?: IFilter[];
  filterOptions: IFilterOptionsItem[];
  setFilters?: (filters?: IFilter[]) => void;
  onClearAll: () => void;
  onSelectionUpdate?: (count: number) => void;
  total?: number;
  entityName?: string;
}

const FiltersSidebar = ({
  isOpen,
  isMobile,
  showCounts = true,
  onClose,
  filters,
  filterOptions,
  setFilters,
  onClearAll,
  onSelectionUpdate,
  total,
  entityName = 'offerring'
}: FiltersSidebar) => {
  const store = useStore();
  const [isResetDisabled, setIsResetDisabled] = useState(false);

  const onSubmitFilters = useCallback(
    (filters?: IFilter[]) => {
      const category = store.MarketplaceStore.getFilters?.map((filter) => filter.field).join(',');
      logEvent('search', 'filter', `filter: ${category}`);
      setFilters && setFilters(filters);
    },
    [setFilters, store.MarketplaceStore]
  );

  useEffect(() => {
    const selectedFiltersCount = filterOptions.reduce((previousValue, { filterValues }) => {
      if (!filterValues) return 0;
      return previousValue + filterValues.filter(({ isSelected }) => isSelected).length;
    }, 0);
    setIsResetDisabled(selectedFiltersCount === 0);
    if (onSelectionUpdate) {
      onSelectionUpdate(selectedFiltersCount);
    }
  }, [filterOptions, onSelectionUpdate]);

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant={isMobile ? 'temporary' : 'persistent'}
      PaperProps={{ elevation: 9 }}
      classes={{
        paper: s.FiltersSidebar__paper
      }}
    >
      <div className={s.FiltersSidebar__head}>
        <Typography variant="h3">Filters</Typography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={s.FiltersSidebar__main}>
        {filterOptions.map(({ filterType, filterValues, keyName }) => (
          <FiltersSidebarItem
            key={filterType}
            filterType={filterType}
            showCounts={showCounts}
            keyName={keyName}
            filterValues={filterValues}
            filters={filters?.filter(({ field }) => field == keyName)}
            setFilters={onSubmitFilters}
          />
        ))}
      </div>
      <div className={s.FiltersSidebar__controls}>
        <Button className={s.FiltersSidebar__results} onClick={onClose}>
          Show {total} {total === 1 ? entityName : pluralize(entityName)}
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setIsResetDisabled(true);
            onClearAll();
          }}
        >
          Clear all
        </Button>
      </div>
    </Drawer>
  );
};

export default observer(FiltersSidebar);
